import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  HStack,
  IconButton,
  useToast,
  InputGroup,
  InputLeftElement,
  Text,
  Badge,
  Avatar,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '@chakra-ui/icons';
import TableSkeleton from '../../components/TableSkeleton';
import FloatingAddButton from '../../components/FloatingAddButton';

interface UsuarioTableProps {
  onOpenDetail: (id: string | null) => void;
}

const UsuarioTable: React.FC<UsuarioTableProps> = ({ onOpenDetail }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/usuarios?page=${page}&pageSize=${pageSize}&search=${searchQuery}`
      );
      const data = await response.json();
      setUsuarios(data.data);
      setTotalPages(data.pagination.totalPages);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los usuarios.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, [page, searchQuery]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  return (
    <Box p={4} pt={79} width="100%">
      {/* Barra de búsqueda */}
      <Box mb={6}>
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input 
            placeholder="Buscar usuarios..." 
            value={searchQuery} 
            onChange={handleSearch}
            bg="white"
            borderColor="gray.200"
            _focus={{ borderColor: "#bb8c52", boxShadow: "0 0 0 1px #bb8c52" }}
          />
        </InputGroup>
      </Box>

      {/* Tabla moderna */}
      {loading ? (
        <TableSkeleton rows={pageSize} columns={5} hasAvatar={true} />
      ) : (
        <Box 
          bg="white" 
          borderRadius="lg" 
          boxShadow="sm" 
          border="1px solid" 
          borderColor="gray.200"
          overflow="hidden"
          mb={6}
        >
          <Box overflowX="auto">
            <Table variant="simple" size="md">
              <Thead bg="gray.50">
                <Tr>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">Usuario</Th>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">Email</Th>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">Nombre de Usuario</Th>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">Estado</Th>
                  <Th color="#681a2d" fontWeight="bold" fontSize="sm">ID</Th>
                </Tr>
              </Thead>
              <Tbody>
                {usuarios.map((usuario: any) => (
                  <Tr
                    key={usuario.id}
                    onClick={() => onOpenDetail(usuario.id)}
                    cursor="pointer"
                    _hover={{ 
                      bg: "#bb8c5210"
                    }}
                  >
                    <Td>
                      <HStack spacing={3}>
                        <Avatar 
                          size="sm" 
                          name={`${usuario.nombre} ${usuario.primerApellido}`}
                          bg="#5b182b"
                          color="white"
                        />
                        <Box>
                          <Text fontWeight="semibold" color="#681a2d" fontSize="sm">
                            {usuario.nombre} {usuario.primerApellido}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {usuario.segundoApellido}
                          </Text>
                        </Box>
                      </HStack>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color="gray.600">
                        {usuario.email || '-'}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" fontWeight="medium">
                        {usuario.usuario}
                      </Text>
                    </Td>
                    <Td>
                      <Badge 
                        bg={usuario.activo ? '#28a745' : '#dc3545'}
                        color="white" 
                        fontSize="xs"
                        borderRadius="full"
                        px={2}
                      >
                        {usuario.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge 
                        bg="#bb8c52" 
                        color="white" 
                        fontSize="xs"
                        borderRadius="full"
                        px={2}
                      >
                        #{usuario.id}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}

      {/* Paginación */}
      <HStack spacing={4} justify="center">
        <IconButton
          icon={<ChevronLeftIcon />}
          onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          isDisabled={page === 1}
          aria-label="Página anterior"
          variant="outline"
          borderColor="#bb8c52"
          color="#bb8c52"
          _hover={{ bg: "#bb8c52", color: "white" }}
          size="sm"
        />
        <Text color="#681a2d" fontWeight="medium" fontSize="sm">
          Página {page} de {totalPages}
        </Text>
        <IconButton
          icon={<ChevronRightIcon />}
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
          isDisabled={page === totalPages}
          aria-label="Página siguiente"
          variant="outline"
          borderColor="#bb8c52"
          color="#bb8c52"
          _hover={{ bg: "#bb8c52", color: "white" }}
          size="sm"
        />
      </HStack>
      
      <FloatingAddButton 
        text="Nuevo Usuario" 
        onClick={() => onOpenDetail(null)} 
      />
    </Box>
  );
};

export default UsuarioTable;
